import type { z } from "zod";
import type { LayoutElement } from "@/lib/layoutRenderer/schemas/layout.schema";
import type { FieldDefinition } from "@/services/form-definition/form-definition-core.types";
import type { ApiNumberDataType } from "../constants/api-number-data-type.constant";
import type {
	DynamicDateOption,
	DynamicTimeOption,
} from "../constants/dynamicForm.constant";

/**
 * Logical join across a rule's conditions. All conditions in a single rule share
 * the same join — there is no mixed AND/OR within one rule.
 */
export const FormRuleConditionLogic = {
	all: "all",
	any: "any",
} as const;
export type FormRuleConditionLogicValue =
	(typeof FormRuleConditionLogic)[keyof typeof FormRuleConditionLogic];

/**
 * Operators a single form-rule condition can use. The allowed operator set for a
 * given field `dataType` is constrained at the editor UI layer.
 */
export const FormRuleConditionOperator = {
	equals: "equals",
	notEquals: "not_equals",
	contains: "contains",
	notContains: "not_contains",
	greaterThan: "greater_than",
	lessThan: "less_than",
	greaterThanOrEqual: "greater_than_or_equal",
	lessThanOrEqual: "less_than_or_equal",
	isEmpty: "is_empty",
	isNotEmpty: "is_not_empty",
	isChecked: "is_checked",
	isNotChecked: "is_not_checked",
	in: "in",
	notIn: "not_in",
} as const;
export type FormRuleConditionOperatorValue =
	(typeof FormRuleConditionOperator)[keyof typeof FormRuleConditionOperator];

/**
 * Side-effect a rule applies to a target field when it fires.
 */
export const FormRuleActionType = {
	showField: "show_field",
	hideField: "hide_field",
	enableField: "enable_field",
	disableField: "disable_field",
	setRequired: "set_required",
	setOptional: "set_optional",
	setValidation: "set_validation",
} as const;
export type FormRuleActionTypeValue =
	(typeof FormRuleActionType)[keyof typeof FormRuleActionType];

// Listener action types
export type ListenerActionType =
	| "clear_field"
	| "set_field_value"
	| "toggle_checkbox"
	| "filter_options"
	| "enable_field"
	| "disable_field"
	| "show_field"
	| "hide_field"
	| "set_required"
	| "set_optional";

export type FieldType =
	| "text"
	| "textarea"
	| "number"
	| "phone"
	| "url"
	| "checkbox"
	| "radio"
	| "select"
	| "slider"
	| "switch"
	| "toggleButton"
	| "autocomplete"
	| "group"
	| "fieldArray"
	| "date"
	| "time"
	| "dateTime"
	| "file"
	| "multiSelect"
	| "users"
	| "signature"
	| "address";

// Listener action interface
export interface ListenerAction {
	type: ListenerActionType;
	targetFieldId: string; // The field name to target
	value?:
		| string
		| number
		| boolean
		| string[]
		| Record<string, string | number | boolean>;
	filterCondition?: {
		field: string; // The field name to filter by
		operator: "equals" | "contains" | "starts_with" | "ends_with";
		value: string;
	};
}

// Field listener interface
export interface FieldListener {
	id: string;
	name: string;
	description?: string;
	sourceFieldId: string; // The field name that triggers this listener
	event: "onChange" | "onBlur" | "onMount" | "onSubmit";
	debounceMs?: number;
	actions: ListenerAction[];
}

/**
 * A single condition compared against a form value at runtime.
 *
 * @property fieldDefinitionId - The `id` of the {@link FormField} whose value drives this condition.
 * @property operator - Comparison operator; the allowed set is constrained by the field's data type.
 * @property value - Right-hand side of the comparison; absent for operators like `is_empty` / `is_checked`.
 */
export interface FormRuleCondition {
	id: string;
	fieldDefinitionId: string;
	operator: FormRuleConditionOperatorValue;
	value?: string | number | boolean | string[];
}

/**
 * Side-effect applied to one target field when its parent {@link FormRule} fires.
 *
 * @property type - Which behavior to apply (show/hide/enable/disable/required/optional/setValidation).
 * @property targetFieldDefinitionId - The `id` of the {@link FormField} to affect.
 * @property value - Required only for `set_validation` (a {@link FieldValidation} object).
 */
export interface FormRuleAction {
	id: string;
	type: FormRuleActionTypeValue;
	targetFieldDefinitionId: string;
	value?: string | number | boolean | string[] | FieldValidation;
}

/**
 * A reactive rule that watches one or more field values and applies actions to one or more target fields.
 *
 * @property enabled - When `false`, the rule is skipped at runtime. Defaults to `true` when omitted.
 * @property conditionLogic - `"all"` requires every condition to match (AND); `"any"` requires at least one (OR).
 * @property conditions - When empty, the rule is treated as unconditionally true (actions always apply).
 */
export interface FormRule {
	id: string;
	name: string;
	description?: string;
	enabled?: boolean;
	conditionLogic: FormRuleConditionLogicValue;
	conditions: FormRuleCondition[];
	actions: FormRuleAction[];
	createdAt?: string;
	updatedAt?: string;
}

// Base field interface
export interface BaseField {
	id: string;
	name: string;
	label: string;
	required?: boolean;
	disabled?: boolean;
	hidden?: boolean;
	/** Optional helper text displayed below the field label. */
	hint?: string;
	/** Rules scoped to this field (target this field's id by default). */
	rules?: FormRule[];
	dependentRules?: string[]; // IDs of rules that depend on this field
	listeners?: FieldListener[]; // Field listeners for reactive behavior
}

// Text field interface
export interface TextField extends BaseField {
	type: "text";
	placeholder?: string;
	validation?: {
		minLength?: number;
		maxLength?: number;
		rows?: number;
		pattern?: string;
		custom?: string; // Custom validation message
	};
}

// Textarea field interface
export interface TextAreaField extends BaseField {
	type: "textArea";
	placeholder?: string;
	validation?: {
		minLength?: number;
		maxLength?: number;
		rows?: number;
		pattern?: string;
		custom?: string; // Custom validation message
	};
}

// Number field interface with validation
export interface NumberField extends BaseField {
	type: "number";
	/** API `FieldDefinition.dataType`: {@link ApiNumberDataType}. */
	apiNumberDataType?: ApiNumberDataType;
	placeholder?: string;
	validation?: {
		min?: number;
		max?: number;
		step?: number;
		custom?: string;
	};
}

// Telephone field interface
export interface TelephoneField extends BaseField {
	type: "phone";
	placeholder?: string;
}

// URL field interface
export interface UrlField extends BaseField {
	type: "url";
	placeholder?: string;
	validation?: {
		minLength?: number;
		maxLength?: number;
		pattern?: string;
		custom?: string;
	};
}

// Checkbox field interface
export interface CheckboxField extends BaseField {
	type: "checkbox";
	defaultChecked?: boolean;
}

// Option interface for fields with options
export interface FieldOption {
	value: string;
	label: string;
	[key: string]: string | number | boolean; // Allow additional properties for filtering, etc.
}

// Radio field interface
export interface RadioField extends BaseField {
	type: "radio";
	options: FieldOption[];
	defaultValue?: string;
}

// Select field interface
export interface SelectField extends BaseField {
	type: "select";
	options: FieldOption[];
	defaultValue?: string;
	multiple?: boolean;
	placeholder?: string;
}

// Slider field interface
export interface SliderField extends BaseField {
	type: "slider";
	min: number;
	max: number;
	step?: number;
	defaultValue?: number;
	marks?: Array<{
		value: number;
		label: string;
	}>;
}

// Switch field interface
export interface SwitchField extends BaseField {
	type: "switch";
	defaultChecked?: boolean;
}

// Toggle button field interface
export interface ToggleButtonField extends BaseField {
	type: "toggleButton";
	options: FieldOption[];
	defaultValue?: string;
	exclusive?: boolean; // true for radio-like, false for checkbox-like
}

// Autocomplete field interface
export interface AutocompleteField extends BaseField {
	type: "autocomplete";
	options: FieldOption[];
	defaultValue?: string | string[];
	multiple?: boolean;
	freeSolo?: boolean; // Allow custom values
	placeholder?: string;
}

// Group field interface for nested fields
export interface GroupField extends BaseField {
	type: "group";
	fields: FormField[];
	accordion?: {
		enabled: boolean;
		defaultExpanded: boolean;
		/** Optional cap on nested fields (e.g. form builder section limit). */
		maxItems?: number;
	};
}

// Field array interface for dynamic arrays of field groups
export interface FieldArrayField extends BaseField {
	type: "fieldArray";
	fields: FormField[]; // The fields that will be repeated for each array item
	minItems?: number; // Minimum number of items required
	maxItems?: number; // Maximum number of items allowed
	addButtonText?: string; // Custom text for the add button
	removeButtonText?: string; // Custom text for the remove button
	itemLabel?: string; // Label for each item (e.g., "User", "Item")
}

export interface DatePickerField extends BaseField {
	type: "date";
	placeholder?: string;
	defaultValue?: string; // ISO string format
	validation?: {
		min?: string | DynamicDateOption; // ISO string format or dynamic option for minimum date
		max?: string | DynamicDateOption; // ISO string format or dynamic option for maximum date
		custom?: string; // Custom validation message
	};
}

export interface TimePickerField extends BaseField {
	type: "time";
	placeholder?: string;
	defaultValue?: string; // ISO string format
	validation?: {
		min?: string | DynamicTimeOption; // ISO string format or dynamic option for minimum time
		max?: string | DynamicTimeOption; // ISO string format or dynamic option for maximum time
		custom?: string; // Custom validation message
	};
}

export interface DateTimePickerField extends BaseField {
	type: "dateTime";
	placeholder?: string;
	defaultValue?: string; // ISO string format
	validation?: {
		min?: string | DynamicDateOption; // ISO string format or dynamic option for minimum date/time
		max?: string | DynamicDateOption; // ISO string format or dynamic option for maximum date/time
		custom?: string; // Custom validation message
	};
}

/** Serializable staged file row (matches app file-upload list items). */
export interface FileUploadFieldValueItem {
	id: string;
	name: string;
	size: number;
	type: string;
	progress: number;
	failed?: boolean;
}

export interface FileUploadField extends BaseField {
	type: "file";
	/**
	 * `dropZone` — large click + drag-and-drop area.
	 * `button` — compact button that opens the file picker only.
	 * @default "dropZone"
	 */
	presentation?: "dropZone" | "button";
	/** Optional helper text below the field label. */
	hint?: string;
	/** Passed to the file input's `accept` attribute (e.g. `image/*,.pdf`). */
	accept?: string;
	/** When false, choosing or dropping files replaces the current file. @default true */
	multiple?: boolean;
	/** Primary line in the drop zone. */
	dropZoneTitle?: string;
	/** Secondary line in the drop zone. */
	dropZoneHint?: string;
	validation?: {
		/** Maximum number of files when `multiple` is true. */
		maxFiles?: number;
		custom?: string;
	};
}

export interface MultiSelectField extends BaseField {
	type: "multiSelect";
	options: FieldOption[];
	placeholder?: string;
}

export interface UsersField extends BaseField {
	type: "users";
	placeholder?: string;
}

export interface SignatureField extends BaseField {
	type: "signature";
}

export interface AddressField extends BaseField {
	type: "address";
	placeholder?: string;
}

// Union type for all field types
export type FormField =
	| TextField
	| TextAreaField
	| NumberField
	| TelephoneField
	| UrlField
	| CheckboxField
	| RadioField
	| SelectField
	| SliderField
	| SwitchField
	| ToggleButtonField
	| AutocompleteField
	| GroupField
	| FieldArrayField
	| DatePickerField
	| TimePickerField
	| DateTimePickerField
	| FileUploadField
	| MultiSelectField
	| UsersField
	| SignatureField
	| AddressField;

/**
 * OpenAPI {@link FieldDefinition} plus UI fields required for {@link FieldRenderer} and round-trip import.
 */
export type PersistedDynamicFieldDefinition = FieldDefinition & {
	options?: FieldOption[];
	placeholder?: string;
	disabled?: boolean;
	hidden?: boolean;
	multiple?: boolean;
	/** Checkbox fields only — maps to {@link CheckboxField.defaultChecked}. */
	defaultChecked?: boolean;
	defaultValue?: string | number | boolean;
	exclusive?: boolean;
	presentation?: FileUploadField["presentation"];
	hint?: string;
	accept?: string;
	dropZoneTitle?: string;
	dropZoneHint?: string;
	validation?: FieldValidation | FileUploadField["validation"];
};

// Main schema interface
export interface DynamicFormSchema {
	id: string;
	title?: string;
	description?: string;
	fieldDefinitions: PersistedDynamicFieldDefinition[];
	/** Global rules that can affect multiple fields. */
	rules?: FormRule[];
	// layout: LayoutElement;
	layoutJson: LayoutElement;
}

// Validation configuration interface
export interface FieldValidation {
	min?: number;
	max?: number;
	required?: boolean;
	pattern?: string;
	minLength?: number;
	maxLength?: number;
	rows?: number;
	custom?: string;
}

// Form field with validation interface
export interface FormFieldWithValidation extends BaseField {
	validation?: FieldValidation;
}

// Zod schema mapping result type
export type ZodSchemaResult = z.ZodObject<Record<string, z.ZodTypeAny>>;

// Field renderer props interface
// TODO: Improve typing to avoid any types
export interface FieldRendererProps {
	field: FormField;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: any; // Using any for now to avoid complex TanStack Form generic types
	onFieldChange?: (
		fieldName: string,
		value: string | number | boolean | string[],
	) => void;
	editorMode?: boolean;
}

// Field array operations interface
export interface FieldArrayOperations {
	add: () => void;
	remove: (index: number) => void;
	move: (fromIndex: number, toIndex: number) => void;
	insert: (index: number) => void;
}

// Form submission value type - using a more specific definition to avoid circular reference
export type FormValue = Record<string, unknown>;

// Rule evaluation context
export interface RuleEvaluationContext {
	formValues: Record<string, unknown>;
	fieldId: string;
}

// Rule evaluation result
export interface RuleEvaluationResult {
	shouldShow: boolean | undefined;
	shouldEnable: boolean | undefined;
	isRequired: boolean | undefined;
	validation?: FieldValidation;
}
